import $ from 'jquery';
import { displayDevMessages } from '../mpro/modules/mpro.error-handling.js';
import { isPlainObject } from '../util/types.js';
import { iterableToJson } from '../util/data.js';

const defaultRequestTimeout = 30_000;

/**
 * Custom Xhr Error type
 */
export class XhrError extends Error {

	static NAME = 'XhrError';

	/**
	 * @param {number} status
	 * @param {string} textStatus
	 * @param {string} message
	 */
	constructor(message, status, textStatus, responseText, responseJSON) {
		super(message);
		this.status = status;
		this.textStatus = textStatus;
		this.responseText = responseText;
		this.responseJSON = responseJSON;
		// eslint-disable-next-line unicorn/custom-error-definition
		this.name = XhrError.NAME;
	}

	toString() {
		/** @type {any} */
		// const ctr = this.constructor;
		return `${this.name} (status: ${this.status}, textStatus: ${this.textStatus}): ${this.message}`;
	}
}

export class AbortError extends DOMException {

	static NAME = 'AbortError';

	/**
	 * @param {string} message
	 */
	constructor(message = 'The operation was aborted.') {
		// @ts-ignore
		super(message, AbortError.NAME, DOMException.ABORT_ERR);
	}

	toString() {
		/** @type {any} */
		// const ctr = this.constructor;
		return `${this.name}: ${this.message}`;
	}
}

export class TimeoutError extends DOMException {

	static NAME = 'TimeoutError';

	/**
	 * @param {string} message
	 */
	constructor(message = 'The operation timed out.') {
		// @ts-ignore
		super(message, TimeoutError.NAME, DOMException.TIMEOUT_ERR);
	}

	toString() {
		/** @type {any} */
		// const ctr = this.constructor;
		return `${this.name}: ${this.message}`;
	}
}

const cache = new Map();

/**
 * @param {string | URL} url
 * @param {ExtendedAjaxOptions} options
 * @param {'xhr' | 'fetch'} type
 */
const normalizeOptions = (url, options, type = 'fetch') => {

	options.headers ||= Object.create(null);

	if (!options.method) {
		options.method = 'GET';
	}
	options.method = options.method.toUpperCase();

	// Data type being sent to the server
	if (!options.requestDataType) {
		options.requestDataType = 'params';
	}

	// Data type expected from the server
	if (!options.dataType) {
		options.dataType = 'json';
	}
	// Normalize dataType for xhr
	else if ((type === 'xhr') && ['arraybuffer', 'blob'].includes(options.dataType)) {
		options.xhrFields = {
			// @ts-ignore
			responseType: options.dataType,
		};
		options.dataType = 'binary';
	}

	// No cache
	if (!('cache' in options)) {
		options.cache = false;
	}

	// Default timeout
	if (!('timeout' in options)) {
		options.timeout = defaultRequestTimeout;
	}

	// Disable jQuery.ajax global events
	options.global = false;
	// Disable jQuery data processing
	options.processData = false;
	// Disable jQuery data serialization
	options.contentType = false;

	// Final post processing
	const { method, requestDataType, dataType, headers } = options;
	const sendBody = ['POST', 'PUT', 'PATCH'].includes(method);

	// Prepare the request data
	const data = normalizeData(options.data, requestDataType, sendBody);

	// Set Accept header
	if (dataType === 'json') {
		headers.Accept = 'application/json';
	}

	// Set Content-Type header
	if ((requestDataType === 'json') && (sendBody === true)) {
		headers['Content-Type'] = 'application/json;charset=UTF-8';
	}
	// The request automatically sets the content-type header to multipart/form-data, if the data is a FormData object and data is sent through the body
	else if (!sendBody || ((sendBody === true) && ((data instanceof FormData) === false))) {
		headers['Content-Type'] = 'application/x-www-form-urlencoded;charset=UTF-8';
	}

	// Mask as xhr for server-side processing to be correct
	headers['X-Requested-With'] = 'XMLHttpRequest';

	// Prepare the request URL
	url = (url instanceof URL) ? url : new URL(url, document.URL);

	// Append query params to url
	if (sendBody === false) {

		if (data && (data instanceof URLSearchParams)) {

			const urlParams = new URLSearchParams(url.search);

			for (const [name, value] of data) {

				// Overwrite existing params
				if (url.searchParams.has(name)) {
					url.searchParams.delete(name);
				}

				// Append to existing params
				urlParams.append(name, value);
			}

			// Set combined search string
			url.search = urlParams.toString();

			// eslint-disable-next-line require-atomic-updates
			options.data = null;
		}
		// Just send the data as is (should not happen)
		else {
			options.data = data;
		}
	}
	// Send data through request body
	else {
		options.data = data;
	}

	// eslint-disable-next-line require-atomic-updates
	options.url = url;

	// console.log('options', options);
	// console.log('data', data);

	return options;
};

/**
 * @param {RequestDataInit} data
 * @param {RequestDataType} requestDataType
 * @param {boolean} sendBody
 *
 * @returns {string | URLSearchParams | FormData}
 */
export const normalizeData = (data, requestDataType, sendBody) => {

	/** @type {string} */
	let typeOfData = typeof data;

	if (typeOfData === 'undefined') {
		// @ts-ignore
		return data;
	}

	if (typeOfData === 'object') {
		if (data instanceof FormData) {
			typeOfData = 'formdata';
		}
		else if (data instanceof URLSearchParams) {
			typeOfData = 'urlsearchparams';
		}
		else if (data instanceof Map) {
			typeOfData = 'map';
		}
		else if (data instanceof Blob) {
			typeOfData = 'blob';
		}
		if (Array.isArray(data)) {
			typeOfData = 'array';
		}
		else if (isPlainObject(data)) {
			typeOfData = 'plainobject';
		}
	}

	// Send everything as url encoded params
	if (requestDataType === 'params') {
		if (data === null) {
			// @ts-ignore
			return data;
		}
		// Convert to URLSearchParams
		else if (['map', 'array', 'string', 'number', 'boolean'].includes(typeOfData)) {
			// Note: if array is provided, it must be an entries type array, not standard jQuery params array (with {name, value} objects)
			// @ts-ignore
			return new URLSearchParams(data);
		}
		// Interpret plain object as $.param object
		else if (typeOfData === 'plainobject') {
			// @ts-ignore
			return new URLSearchParams($.param(data));
		}

		// Send through request body
		if (sendBody === true) {
			switch (typeOfData) {
				case 'blob': {
					const fd = new FormData();
					// @ts-ignore
					fd.append('file', data);
					return fd;
				}
				case 'formdata':
				case 'urlsearchparams': {
					// @ts-ignore
					return data;
				}
				default: {
					throw new TypeError('Invalid data provided for request');
				}
			}
		}
		// Send through query string (convert iterable objects to URLSearchParams or throw)
		else {
			// @ts-ignore
			return new URLSearchParams(data);
		}
	}
	// Everything else is sent JSON encoded string in the request body
	else {
		// Send through request body
		if (sendBody === true) {

			// Standard JSON
			// @ts-ignore
			if ((data === null) || ['plainobject', 'array', 'string', 'number', 'boolean'].includes(typeOfData) || (typeof data.toJSON === 'function')) {
				return JSON.stringify(data);
			}
			// Is iterable object (FormData, URLSearchParams, Map)
			else if (typeof data[Symbol.iterator] === 'function') {
				// @ts-ignore
				// eslint-disable-next-line require-atomic-updates
				return JSON.stringify(iterableToJson(data));
			}
			// File upload as JSON
			else if (data instanceof Blob) {
				// eslint-disable-next-line require-atomic-updates
				return JSON.stringify({});
			}

			// Other type of object that cannot be converted to JSON
			throw new TypeError('Invalid data provided for request');
		}
		// Cannot send data with GET request
		else {
			throw new TypeError('Invalid data provided for request');
		}
	}

	// @ts-ignore
	return data;
};

/**
 * @param {ExtendedAjaxOptions} options
 */
const buildCacheKey = (options) => {

	// Only GET requests can be cached
	if (options.method !== 'GET') {
		return null;
	}

	let key = `${options.url}~${options.method}~${options.requestDataType}~${options.dataType}`;
	const { data } = options;

	if (data) {

		// String data
		if (['string', 'number', 'boolean'].includes(typeof data)) {
			key += `~${data}`;
		}
		// Not string data
		else if (Array.isArray(data) || isPlainObject(data)) {
			key += `~${JSON.stringify(data)}`;
		}
		// Is iterable object (FormData, URLSearchParams, etc.)
		// @ts-ignore
		else if (typeof data[Symbol.iterator] === 'function') {
			// @ts-ignore
			key += `~${JSON.stringify(Object.fromEntries(data))}`;
		}
		// Cannot build cache key
		else {
			return null;
		}
	}

	return key;
};

/**
 * @param {string} text
 */
export const parseErrorContent = (text) => {

	// Seems like json
	if (/(?:"(?:title|success|content|location|message|reload|formValues)":\s*)/.test(text)) {

		let extra;
		let extraBefore;
		let extraAfter;
		let json_str;
		let json_data;

		let groups;
		if ((groups = /([\S\s]*)({\s*"(?=title|success|content|location|message|reload|formValues)[\S\s]+})([\S\s]*$)/.exec(text))) {
			[, extraBefore, json_str, extraAfter = ''] = groups;
			let msgToken;

			// The json string comes first, we need to clean it up
			if ((!extraBefore || extraBefore.trim() === '') && (msgToken = /<!--(\s*(?:MSG_DEBUG|ERR_ALERT)\s*)-->/.exec(json_str))) {
				// For now, we can only detect debug/alert messages
				const [, token] = msgToken;
				const splitToken = `<!--${token}-->`;
				const parts = json_str.split(splitToken);
				if (parts.length > 1) {
					json_str = parts.shift();
					extra = splitToken + parts.join(splitToken) + extraAfter;
				}
			}
			else {
				extra = extraBefore + extraAfter;
			}
		}

		if (!json_str) {
			return null;
		}

		try {
			json_data = JSON.parse(json_str);
		}
		catch {
			json_data = {
				content: '',
			};
		}

		json_data.extra = extra;

		return json_data;
	}

	return null;
};

/**
 * Execute xhr request
 *
 * @param {string|URL} url
 * @param {ExtendedAjaxOptions} jqAjaxOptions
 * @param {RemoteOptions} options
 */
export const doXhr = async (url, jqAjaxOptions = {}, options = {}) => {

	// Normalize ajax options
	// eslint-disable-next-line require-atomic-updates
	jqAjaxOptions = normalizeOptions(url, jqAjaxOptions, 'xhr');
	// jQuery does not support the URL object
	// @ts-ignore
	jqAjaxOptions.url = jqAjaxOptions.url.toString();

	// Cache key
	const doCache = options.cache === true;
	const cacheKey = doCache ? buildCacheKey(jqAjaxOptions) : null;
	// console.log('cacheKey', cacheKey);

	// Return from cache
	if (cacheKey && cache.has(cacheKey)) {
		return cache.get(cacheKey);
	}

	// @ts-ignore
	const xhr = $.ajax(jqAjaxOptions);
	const result = xhr.then((res) => {

		// Save in cache
		if (doCache && cacheKey) {
			cache.set(cacheKey, res);
		}

		// Resolve the response
		return res;

		// eslint-disable-next-line unicorn/catch-error-name
	}, (jqXhr, textStatus, error) => {

		// Normalize the timeout error
		if (error === 'timeout') {
			const err = new TimeoutError();
			throw new XhrError(err.message, err.code ?? 0, err.name || error, jqXhr.responseText, jqXhr.responseJSON);
		}
		// Normalize the abort error
		else if (error === 'abort') {
			const err = new AbortError();
			throw new XhrError(err.message, err.code ?? 0, err.name || error, jqXhr.responseText, jqXhr.responseJSON);
		}

		// Parse json
		if (jqAjaxOptions.dataType === 'json') {

			// Try to separate the json from the extra markup
			const extraJson = parseErrorContent(jqXhr.responseText);

			// Parse Error
			if (extraJson === null) {
				throw new XhrError(error, jqXhr.status, textStatus, jqXhr.responseText, jqXhr.responseJSON);
			}

			// Show extra content
			if (extraJson.extra) {
				displayDevMessages(extraJson.extra, {
					title: `Extra content from the last xhr request (${jqAjaxOptions.url})`,
					// preventNavigation: true,
					preventNavigation: false,
				});
			}

			const result = extraJson || jqXhr.responseText;

			// Save in cache
			if (doCache && cacheKey) {
				cache.set(cacheKey, result);
			}

			// Resolve the response
			return result;
		}

		// Non json response
		throw new XhrError(error, jqXhr.status, textStatus, jqXhr.responseText, jqXhr.responseJSON);
	});

	// Assign the abort method to ref
	if (options.ref) {
		options.ref.abort = () => xhr.abort();
		options.ref.getResponseHeader = name => xhr.getResponseHeader(name);
		options.ref.getAllResponseHeaders = () => xhr.getAllResponseHeaders();
	}

	return await result;
};

/**
 * Execute fetch request
 *
 * @param {string|URL} url
 * @param {ExtendedAjaxOptions} jqAjaxOptions
 * @param {RemoteOptions} options
 */
export const doFetch = async (url, jqAjaxOptions = {}, options = {}) => {

	// Normalize ajax options
	jqAjaxOptions = normalizeOptions(url, jqAjaxOptions, 'fetch');

	// Cache key
	const doCache = options.cache === true;
	const cacheKey = doCache ? buildCacheKey(jqAjaxOptions) : null;
	// console.log('cacheKey', cacheKey);

	// Return from cache
	if (cacheKey && cache.has(cacheKey)) {
		return cache.get(cacheKey);
	}

	/** @type {RequestInit} */
	const fetchOpt = Object.create(null);
	fetchOpt.headers = { ...jqAjaxOptions.headers };
	fetchOpt.method = jqAjaxOptions.method;

	// Set the body
	if (jqAjaxOptions.data) {
		// @ts-ignore
		fetchOpt.body = jqAjaxOptions.data;
	}

	// Create an abort controller
	const controller = new AbortController();
	// Pass the signal to fetch
	fetchOpt.signal = controller.signal;

	// Set the cache buster
	if (jqAjaxOptions.cache === true) {
		jqAjaxOptions.url.searchParams.append('_', String(Date.now()));
	}

	let timer;

	// Set the timeout
	if (jqAjaxOptions.timeout > 0) {
		timer = setTimeout(() => {
			controller.abort(new TimeoutError());
		}, jqAjaxOptions.timeout);
	}

	// AbortSignal.timeout();

	// Init the fetch request
	// const request = new Request(jqAjaxOptions.url, fetchOpt);

	// Pass the ref to the controller
	if (options.ref) {
		options.ref.abort = () => controller.abort(new AbortError());
		options.ref.getResponseHeader = () => '';
		options.ref.getAllResponseHeaders = () => '';
	}

	try {
		const response = await fetch(jqAjaxOptions.url, fetchOpt);

		// Clear the timeout timer
		timer && clearTimeout(timer);

		// Pass the ref to the controller
		if (options.ref) {
			options.ref.getResponseHeader = name => response.headers.get(name);
			options.ref.getAllResponseHeaders = () => {
				const headers = [];
				for (const [name, value] of response.headers.entries()) {
					headers.push(`${name}: ${value}`);
				}
				return headers.join('\r\n');
			};
		}

		const dataTypeMethodMap = {
			text: 'text',
			html: 'text',
			xml: 'text',
			script: 'text',
			json: 'text',
			jsonp: 'text',
			blob: 'blob',
			arraybuffer: 'arrayBuffer',
			formdata: 'formData',
		};
		const optDataType = jqAjaxOptions.dataType;
		const responseMethod = dataTypeMethodMap[optDataType] || 'text';

		// Get response body
		const responseData = await response[responseMethod]();
		let json;

		// Http Error
		if (!response.ok) {
			throw new XhrError(response.statusText || 'Status not ok', response.status, 'error', responseData, json);
		}

		// Parse json
		if (typeof responseData === 'string' && optDataType === 'json') {
			try {
				json = JSON.parse(responseData);
			}
			catch (/** @type {any} */ error) {

				// Try to separate the json from the extra markup
				const extraJson = parseErrorContent(responseData);

				// Parse Error
				if (extraJson === null) {
					throw new XhrError(error.message, response.status, 'parseerror', responseData, json);
				}
				else {
					json = extraJson;

					// Show extra content
					if (extraJson.extra) {
						displayDevMessages(extraJson.extra, {
							title: `Extra content from the last fetch request (${jqAjaxOptions.url})`,
							// preventNavigation: true,
							preventNavigation: false,
						});
					}
				}
			}
		}

		const result = json || responseData;

		// Save in cache
		if (doCache && cacheKey) {
			cache.set(cacheKey, result);
		}

		return result;
	}
	// Convert fetch errors to XhrError
	catch (/** @type {any} */ error) {

		// Clear the timeout timer
		timer && clearTimeout(timer);

		if (error?.name === 'AbortError') {
			if (controller.signal.aborted && controller.signal.reason instanceof DOMException) {
				// eslint-disable-next-line no-ex-assign
				error = controller.signal.reason;
			}
		}

		// Re-throw XhrError
		if (error instanceof XhrError) {
			throw error;
		}

		throw new XhrError(error.message, error.code ?? 0, error.name || '');
	}
};